/**
 * useAdminOverviewStats Hook
 *
 * Fetches admin overview statistics (weekly metrics) using URQL query.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AdminOverviewStatsDocument = `
  query AdminOverviewStats {
    adminOverviewStats {
      pendingPayments
      homeGroupAttendanceThisWeek
      activeMinistryMembersThisWeek
      newAccountsThisWeek
      eventsThisWeek
      totalAccounts
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface OverviewStats {
  pendingPayments: number;
  homeGroupAttendanceThisWeek: number;
  activeMinistryMembersThisWeek: number;
  newAccountsThisWeek: number;
  eventsThisWeek: number;
  totalAccounts: number;
}

interface UseAdminOverviewStatsOptions {
  pause?: boolean;
}

interface UseAdminOverviewStatsResult {
  stats: OverviewStats | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch admin overview statistics for the week.
 */
export function useAdminOverviewStats(
  options: UseAdminOverviewStatsOptions = {}
): UseAdminOverviewStatsResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    adminOverviewStats: OverviewStats;
  }>({
    query: AdminOverviewStatsDocument,
    pause,
  });

  return {
    stats: result.data?.adminOverviewStats ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
